/**
 * Settings Modal Component
 *
 * Full-page overlay with sidebar navigation (Discord/Slack pattern)
 * Tabs: Profile, Notifications, Appearance, Privacy, Voice, Security
 */

import { useState, useEffect } from 'react';
import { User, Bell, Palette, Shield, Mic, Lock, LogOut, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { handleTablistArrowKeys } from '@/utils/a11y';
import ProfileSettings from './ProfileSettings';
import NotificationSettings from './NotificationSettings';
import AppearanceSettings from './AppearanceSettings';
import PrivacySettings from './PrivacySettings';
import VoiceSettings from './VoiceSettings';
import SecuritySettings from './SecuritySettings';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'profile' | 'notifications' | 'appearance' | 'privacy' | 'voice' | 'security';
}

type Tab = 'profile' | 'notifications' | 'appearance' | 'privacy' | 'voice' | 'security';

const tabs: { id: Tab; label: string; icon: typeof User }[] = [
  { id: 'profile', label: 'Профил', icon: User },
  { id: 'notifications', label: 'Известия', icon: Bell },
  { id: 'appearance', label: 'Външен вид', icon: Palette },
  { id: 'privacy', label: 'Поверителност', icon: Shield },
  { id: 'voice', label: 'Глас', icon: Mic },
  { id: 'security', label: 'Сигурност', icon: Lock },
];

export const SettingsModal = ({
  isOpen,
  onClose,
  initialTab = 'profile',
}: SettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const { logout } = useAuth();

  // Reset to initial tab when modal opens (adjust state during render on change)
  const openKey = isOpen ? `open:${initialTab}` : 'closed';
  const [prevOpenKey, setPrevOpenKey] = useState(openKey);
  if (openKey !== prevOpenKey) {
    setPrevOpenKey(openKey);
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLogout = async () => {
    onClose();
    await logout();
  };

  const activeTabData = tabs.find((t) => t.id === activeTab);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Настройки"
      className="fixed inset-0 z-50 bg-gray-100 dark:bg-dark-bg-primary animate-modal-in"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close button — top right */}
      <button
        onClick={onClose}
        className="fixed top-6 right-8 z-50 flex items-center gap-2 group"
        aria-label="Затвори настройки"
      >
        <span className="text-[10px] text-gray-400 dark:text-dark-text-muted opacity-0 group-hover:opacity-100 transition-opacity select-none">
          Esc
        </span>
        <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-dark-bg-hover hover:bg-gray-300 dark:hover:bg-dark-bg-input flex items-center justify-center transition-colors">
          <X className="w-4 h-4 text-gray-500 dark:text-dark-text-secondary" />
        </div>
      </button>

      {/* Main layout */}
      <div className="max-w-5xl mx-auto h-full flex py-12 px-8 gap-8">
        {/* Left sidebar navigation */}
        <nav className="w-56 flex-shrink-0 flex flex-col" role="navigation" aria-label="Настройки">
          <div className="flex flex-col gap-1" role="tablist" aria-orientation="vertical" aria-label="Настройки" onKeyDown={handleTablistArrowKeys}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  role="tab"
                  id={`settings-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls="settings-tabpanel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-white dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium shadow-sm'
                      : 'text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary hover:bg-white/50 dark:hover:bg-dark-bg-hover/50'
                  }`}
                >
                  <Icon className="w-[18px] h-[18px]" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Logout at bottom */}
          <div className="mt-auto pt-4 border-t border-gray-200 dark:border-dark-border-secondary">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-[18px] h-[18px]" />
              <span>Изход</span>
            </button>
          </div>
        </nav>

        {/* Right content area */}
        <div
          className="flex-1 overflow-y-auto"
          role="tabpanel"
          id="settings-tabpanel"
          aria-labelledby={`settings-tab-${activeTab}`}
          tabIndex={0}
        >
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">
              {activeTabData?.label}
            </h2>

            {activeTab === 'profile' && <ProfileSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'appearance' && <AppearanceSettings />}
            {activeTab === 'privacy' && <PrivacySettings />}
            {activeTab === 'voice' && <VoiceSettings />}
            {activeTab === 'security' && <SecuritySettings />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
